import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';

export const SITE_NAME = 'Galopa';
export const DEFAULT_SEO_DESCRIPTION =
  'Galopa — trusted sellers, secure transactions, confident riders. Buy and sell secondhand saddles with buyer-protected payments and free resells at 0% commission.';
const DEFAULT_SEO_IMAGE_PATH = 'assets/branding/og-image.webp';
// Hardcoded rather than derived from document.location: during build-time
// prerendering (see app.routes.server.ts), the Angular CLI renders pages
// against an internal localhost server, so document.location.origin would
// otherwise bake `http://localhost:<random port>` into og:url/og:image/
// canonical on every prerendered page. Matches the single prod domain
// already hardcoded in index.html's static OG fallback and the backend's
// sitemap.
const SITE_URL = 'https://galopa.com.au';

export interface SeoMeta {
  title: string;
  description?: string;
  /** Absolute URL. Falls back to the site logo when omitted. */
  image?: string;
}

/**
 * Thin wrapper around Angular's Title/Meta services — also drives Open
 * Graph/Twitter Card tags and the canonical link off the same call, so
 * every page (and shared link) gets a consistent, correct preview. Route-
 * static pages set their SEO tags via `data: { title, description, lcpImage }`
 * (applied centrally from App's router subscription); pages whose title
 * depends on fetched data (e.g. ProductDetailComponent) call `set()` /
 * `setLcpPreload()` directly once that data loads.
 */
@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly titleService = inject(Title);
  private readonly meta = inject(Meta);
  private readonly document = inject(DOCUMENT);

  set({ title, description, image }: SeoMeta): void {
    const resolvedDescription = description ?? DEFAULT_SEO_DESCRIPTION;
    const resolvedImage = image ?? `${SITE_URL}/${DEFAULT_SEO_IMAGE_PATH}`;
    // Strip a trailing slash (except on "/" itself) and any query string —
    // every route in app.routes.ts is registered without one, so a request
    // for e.g. "/shop/" must canonicalize to the same URL as "/shop",
    // otherwise the two serve identical content under two distinct
    // self-referential canonicals and both get indexed as separate pages.
    const { pathname } = this.document.location;
    const normalizedPath = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
    const url = `${SITE_URL}${normalizedPath}`;

    this.titleService.setTitle(title);
    this.setTag('name', 'description', resolvedDescription);

    this.setTag('property', 'og:site_name', SITE_NAME);
    this.setTag('property', 'og:type', 'website');
    this.setTag('property', 'og:title', title);
    this.setTag('property', 'og:description', resolvedDescription);
    this.setTag('property', 'og:image', resolvedImage);
    this.setTag('property', 'og:url', url);

    this.setTag('name', 'twitter:card', 'summary_large_image');
    this.setTag('name', 'twitter:title', title);
    this.setTag('name', 'twitter:description', resolvedDescription);
    this.setTag('name', 'twitter:image', resolvedImage);

    this.setCanonical(url);
  }

  /**
   * Hints the preload scanner at the page's LCP image. A body `<img
   * fetchpriority="high">` is necessary but not sufficient — Lighthouse's
   * "LCP request discovery" audit still wants a head preload so the request
   * starts before the rest of the document is parsed. `href` is a site-
   * relative path or absolute URL; pass `null` to tear the tag down on
   * navigations that have no image LCP.
   */
  setLcpPreload(href: string | null): void {
    const selector = 'link[rel="preload"][as="image"][data-lcp]';
    let link = this.document.querySelector<HTMLLinkElement>(selector);

    if (!href) {
      link?.remove();
      return;
    }

    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'preload');
      link.setAttribute('as', 'image');
      link.setAttribute('data-lcp', '');
      // Sit ahead of the font preloads in index.html so the LCP image
      // competes at the front of the connection pool.
      const fonts = this.document.head.querySelector('link[rel="preload"][as="font"]');
      this.document.head.insertBefore(link, fonts);
    }

    link.setAttribute('href', href);
    link.setAttribute('fetchpriority', 'high');
  }

  private setTag(attr: 'name' | 'property', key: string, content: string): void {
    this.meta.updateTag({ [attr]: key, content });
  }

  private setCanonical(url: string): void {
    let link = this.document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }
}
